import { ExtFile } from "@files-ui/react";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LoadingButton } from "@mui/lab";
import { Box, Button, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import useBulkDashboards from "../../hooks/useBulkDashboards";
import useFileUploader from "../../hooks/useFileUploader";
import { addDashboardQueryFunc, updateDashboardFunc } from "../../lib/helper";
import { Dashboard, DashboardQuerySource } from "../../types/files";
import { AnswerQuestionResponseType } from "../../types/search";
import StageSelector from "../molecules/crunchbase-modal/StageSelector";
import WebsiteUrlInput from "../molecules/crunchbase-modal/WebsiteUrlInput";
import DashboardDeckInput from "../molecules/dashboard-deck/DashboardDeckInput";
import DashboardsSearchOrCreate from "../molecules/dashboards-search-create/DashboardsSearchOrCreate";

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0 32px',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '24px 32px',
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        gap: '12px'
    },
    block: {
        width: '100%',
        height: 'fit-content',
        padding: '12px 16px',
        borderRadius: 16,
        background: 'rgba(240,254,254,0.7)',
        border: `2px solid ${theme.colors.primary['100']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        transition: '0.5s ease-in-out',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem !important',
        fontWeight: '700 !important',
        color: theme.palette.primary.main,
    },
    label: {
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: '600',
        color: '#0f172a',
    },
    link: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        textDecoration: 'none',
        "&:hover": {
            textDecoration: 'none !important',
        },
        cursor: 'pointer',
    },
    cancelButton: {
      color: '#475569',
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: '12px',
      padding: '10px 24px',
      '&:hover': {
        backgroundColor: '#f1f5f9',
      },
    },
    submitButton: {
      background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
      color: '#ffffff',
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: '12px',
      padding: '10px 24px',
      '&:hover': {
        background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      },
      '&:disabled': {
        background: '#e2e8f0',
        color: '#94a3b8',
      },
    },
    success: {
        background: theme.colors.success[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.success[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    failed: {
        background: theme.colors.error[600],
        "&:hover, &:disabled": {
            background: `${theme.colors.error[600]} !important`,
        },
        "& > svg": {
            fill: 'white !important',
        }
    },
    blocked: {
        "&:hover, &:disabled": {
            cursor: 'not-allowed !important',
        },
        cursor: 'not-allowed !important',
    },
}));

const GlobalUrlPattern = /^(?:(?:(?:https?):\/\/)|(?:www\.|(?!www))[A-Za-z0-9.-]+[A-Za-z]{2,}\/?)?[A-Za-z0-9-._~:/?#[\]@!$&'()*+,;=%]+$/i;

const AddEditDashboard: React.FC<{
    collectionKey?: string,
    onClose: () => void,
}> = ({ collectionKey, onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { uploadToS3, docsendIngestion } = useFileUploader({});
    const { addToBulk } = useBulkDashboards();

    const [extFiles, setExtFiles] = useState<ExtFile[]>([]);
    const [status, setStatus] = useState<string>('');
    const [execute, setExecute] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const [customUrl, setCustomUrl] = useState<string>('');
    const [deckLink, setDeckLink] = useState<string>('');
    const [selectedStage, setSelectedStage] = useState<string>('');

    const executeDisabled = useMemo(() => (!name.trim()
        // || (!customUrl && status === 'create')
        || (!loading && status === 'success')
        || ['failed', 'blocked', 'existing'].includes(status)
    ), [name, loading, status]);

    const saveWebQueryAnswer = useCallback(async (dashboard: Dashboard, permalink: string) => {
        if (GlobalUrlPattern.test(permalink)) {
            await addDashboardQueryFunc(
                dashboard!.id,
                'Website',
                userGroup!,
                dashboard!.title,
                JSON.stringify({
                    answer: permalink,
                    type: AnswerQuestionResponseType.URL,
                }),
                undefined,
                301,
                true,
                DashboardQuerySource.CRUNCHBASE,
            );
        }
    }, [userGroup]);

    const handleCreate = useCallback(async (dashboard: Dashboard) => {
        const uploadables = await new Promise<{ blob: Blob; filename: string; }[]>((resolve) => {
            (async () => new Blob([new Uint8Array(await extFiles[0].file!.arrayBuffer())], {type: extFiles[0].type }))()
                .then((blob: Blob) => resolve([{ blob, filename: extFiles[0].name! }]))
                .catch(() => resolve([]));
        });
        const finalUrl = (!customUrl.startsWith('http://') && !customUrl.startsWith('https://'))
            ? 'https://' + customUrl : customUrl;

        Promise.all([
            updateDashboardFunc({...dashboard,
                investmentStage: selectedStage,
                externalLinks: !!deckLink ? [deckLink] : null,
            }),
            saveWebQueryAnswer(dashboard, finalUrl),
            !!extFiles.length ? uploadToS3(dashboard, uploadables)
                : docsendIngestion(dashboard, deckLink || undefined),
        ]).then(() => {
            if (!!collectionKey)
                addToBulk(collectionKey, dashboard);
            onClose();
        }).finally(() => {
            setLoading(false);
            setExecute('');
        });
    // eslint-disable-next-line
    }, [collectionKey, customUrl, deckLink, extFiles, selectedStage]);

    const handleExecute = useCallback(() => {
        setLoading(true);
        setExecute('create');
    }, []);

    return (<>
        <DialogContent className={classes.dialogContent}>
            <Stack direction="column" spacing={3} alignItems="stretch" justifyContent="flex-start" my={3}>
                <Typography className={classes.label}>{'Name'}</Typography>
                <DashboardsSearchOrCreate create squareFit notify execute={execute}
                    onSearch={(search, status) => {setName(search); setStatus(status);}}
                    onCreate={handleCreate} />
                <Typography className={classes.label}>{'Website'}</Typography>
                <WebsiteUrlInput customUrl={customUrl} onCustomUrl={setCustomUrl} inlineCaption />
                <Typography className={classes.label}>{'Deck'}</Typography>
                <DashboardDeckInput deckLink={deckLink} extFiles={extFiles} onUpdate={setExtFiles} onDeckLink={setDeckLink} />
                <Typography className={classes.label}>{'Stage'}</Typography>
                <Box sx={{ transform: 'scale(1.1), translateX(-10px)'}}>
                    <StageSelector onSelectStage={setSelectedStage} />
                </Box>
            </Stack>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
            <Button variant="text"
                className={classes.cancelButton}
                onClick={() => onClose()}
            > {'Cancel'} </Button>
            <LoadingButton variant="text"
                className={classNames(classes.submitButton,
                    (status === 'blocked') && classes.blocked,
                    (!loading && status === 'success') && classes.success,
                    (status === 'failed') && classes.failed,
                )}
                loading={loading}
                disabled={executeDisabled}
                onClick={handleExecute}>
                {(status === 'success') ? (
                    <CheckCircleOutlineIcon />
                ) : (status === 'failed') ? (
                    <ErrorOutlineIcon />
                ) : (
                    <>{'Add'}</>
                )}
            </LoadingButton>
        </DialogActions>
    </>);
}

export default AddEditDashboard;